import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { createTheme, ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import { CssBaseline } from '@mui/material';

// Light theme palette - clean, high-contrast, modern SaaS theme (WCAG AA compliant)
const lightPalette = {
  mode: 'light',
  primary: {
    main: '#2563eb', // Clean, vibrant Royal Blue
    light: '#60a5fa',
    dark: '#1d4ed8',
    contrastText: '#ffffff',
  },
  secondary: {
    main: '#0284c7', // Sky blue accent
    light: '#38bdf8',
    dark: '#0369a1',
    contrastText: '#ffffff',
  },
  background: {
    default: '#f8fafc', // Slate 50
    paper: '#ffffff',
  },
  text: {
    primary: '#0f172a', // Slate 900
    secondary: '#475569', // Slate 600
  },
  divider: '#e2e8f0', // Clean Slate 200 divider
  action: {
    hover: 'rgba(15, 23, 42, 0.04)',
    selected: 'rgba(37, 99, 235, 0.08)',
  },
};

// Dark theme palette (existing neon green / dark cyber)
const darkPalette = {
  mode: 'dark',
  primary: {
    main: '#b4f84d',
    light: '#d9fba1',
    dark: '#84cc16',
  },
  secondary: {
    main: '#3b82f6',
    light: '#93c5fd',
    dark: '#2563eb',
  },
  background: {
    default: '#0a0a0a',
    paper: '#141414',
  },
  text: {
    primary: '#ffffff',
    secondary: '#a3a3a3',
  },
  divider: 'rgba(180, 248, 77, 0.1)',
};

// Shared component overrides
const componentOverrides = {
  MuiButton: {
    styleOverrides: {
      root: {
        borderRadius: 8,
        boxShadow: 'none',
        fontWeight: 600,
        textTransform: 'none',
        '&:hover': {
          boxShadow: '0 2px 8px rgba(37, 99, 235, 0.2)',
        },
      },
      contained: {
        padding: '8px 20px',
        color: '#ffffff',
      },
    },
  },
  MuiPaper: {
    styleOverrides: {
      root: {
        backgroundImage: 'none',
        border: '1px solid #e2e8f0',
      },
      elevation1: {
        boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.06), 0 1px 2px 0 rgba(0, 0, 0, 0.04)',
      },
    },
  },
  MuiCard: {
    styleOverrides: {
      root: {
        borderRadius: 12,
        border: '1px solid #e2e8f0',
        backgroundColor: '#ffffff',
        backgroundImage: 'none',
        boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.06), 0 1px 2px 0 rgba(0, 0, 0, 0.04)',
      },
    },
  },
  MuiDrawer: {
    styleOverrides: {
      paper: {
        borderRight: '1px solid #e2e8f0',
        backgroundColor: '#ffffff',
      },
    },
  },
  MuiAppBar: {
    styleOverrides: {
      root: {
        borderBottom: '1px solid #e2e8f0',
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        color: '#0f172a',
        backdropFilter: 'blur(10px)',
      },
    },
  },
  MuiTableHead: {
    styleOverrides: {
      root: {
        '& .MuiTableCell-head': {
          backgroundColor: '#f1f5f9',
          color: '#334155',
          fontWeight: 700,
        },
      },
    },
  },
  MuiTableCell: {
    styleOverrides: {
      head: {
        borderBottom: '2px solid #cbd5e1',
      },
      body: {
        borderBottom: '1px solid #f1f5f9',
      },
    },
  },
  MuiChip: {
    styleOverrides: {
      root: {
        fontWeight: 600,
        borderRadius: 6,
      },
    },
  },
  MuiTextField: {
    styleOverrides: {
      root: {
        '& .MuiOutlinedInput-root': {
          '&:hover fieldset': {
            borderColor: '#2563eb',
          },
          '&.Mui-focused fieldset': {
            borderColor: '#2563eb',
            borderWidth: 2,
          },
        },
      },
    },
  },
};

const createAppTheme = (mode) => {
  const palette = mode === 'light' ? lightPalette : darkPalette;
  
  // Adjust component overrides for dark mode
  const overrides = { ...componentOverrides };
  if (mode === 'dark') {
    overrides.MuiPaper = {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          backgroundColor: '#141414',
          border: '1px solid rgba(255, 255, 255, 0.05)',
        },
        elevation1: {
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.5)',
        },
      },
    };
    overrides.MuiCard = {
      styleOverrides: {
        root: {
          borderRadius: 16,
          border: '1px solid rgba(180, 248, 77, 0.1)',
          backgroundColor: '#141414',
          background: 'linear-gradient(180deg, #1a1a1a 0%, #111111 100%)',
        },
      },
    };
    overrides.MuiDrawer = {
      styleOverrides: {
        paper: {
          backgroundColor: '#141414',
          borderRight: '1px solid rgba(180, 248, 77, 0.1)',
        },
      },
    };
    overrides.MuiAppBar = {
      styleOverrides: {
        root: {
          backgroundColor: 'rgba(10, 10, 10, 0.8)',
          color: '#ffffff',
          borderBottom: '1px solid rgba(180, 248, 77, 0.1)',
          backdropFilter: 'blur(10px)',
        },
      },
    };
    overrides.MuiTableHead = {
      styleOverrides: {
        root: {
          '& .MuiTableCell-head': {
            backgroundColor: 'rgba(180, 248, 77, 0.1)',
            fontWeight: 600,
          },
        },
      },
    };
    overrides.MuiTableCell = {
      styleOverrides: {
        head: {
          borderBottom: '2px solid rgba(180, 248, 77, 0.2)',
        },
        body: {
          borderBottom: '1px solid rgba(255, 255, 255, 0.04)',
        },
      },
    };
    overrides.MuiTextField = {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            '&:hover fieldset': {
              borderColor: '#b4f84d',
            },
            '&.Mui-focused fieldset': {
              borderColor: '#b4f84d',
            },
          },
        },
      },
    };
    overrides.MuiButton = {
      styleOverrides: {
        root: {
          boxShadow: 'none',
          '&:hover': {
            boxShadow: '0 4px 12px rgba(180, 248, 77, 0.2)',
          },
        },
        contained: {
          padding: '8px 20px',
          color: '#000000',
        },
      },
    };
  }
  
  return createTheme({
    palette,
    typography: {
      fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
      h1: { fontWeight: 700 },
      h2: { fontWeight: 700 },
      h3: { fontWeight: 600 },
      h4: { fontWeight: 600 },
      h5: { fontWeight: 600 },
      h6: { fontWeight: 600 },
      button: { textTransform: 'none', fontWeight: 600 },
    },
    shape: {
      borderRadius: 12,
    },
    components: overrides,
  });
};

const ThemeContext = createContext(null);

export const useThemeMode = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    return {
      mode: 'light',
      toggleTheme: () => {},
      theme: createAppTheme('light'),
    };
  }
  return context;
};

export const ThemeProvider = ({ children }) => {
  const [mode, setMode] = useState(() => {
    // Initialize from localStorage or system preference
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('theme-mode');
      if (saved) return saved;
      if (typeof window.matchMedia === 'function') {
        return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
      }
      return 'light';
    }
    return 'light';
  });

  const forcedLightRoutes = useMemo(() => new Set(['/pos', '/timeclock']), []);

  // Apply theme to document root for CSS variables
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', mode);
    localStorage.setItem('theme-mode', mode);
  }, [mode]);

  // Listen for system theme changes
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return;
    }
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    if (!mediaQuery || !mediaQuery.addEventListener) return;
    const handleChange = (e) => {
      const saved = localStorage.getItem('theme-mode');
      if (!saved) {
        setMode(e.matches ? 'dark' : 'light');
      }
    };
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  const toggleTheme = useCallback(() => {
    setMode((prev) => (prev === 'light' ? 'dark' : 'light'));
  }, []);

  const setTheme = useCallback((newMode) => {
    setMode(newMode);
  }, []);

  const isForcedLight = useCallback((pathname) => {
    return forcedLightRoutes.has(pathname);
  }, [forcedLightRoutes]);

  const theme = useMemo(() => createAppTheme(mode), [mode]);

  const contextValue = useMemo(() => ({
    mode,
    toggleTheme,
    setTheme,
    theme,
    isForcedLight
  }), [mode, toggleTheme, setTheme, theme, isForcedLight]);

  return (
    <ThemeContext.Provider value={contextValue}>
      <MuiThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  );
};

export default ThemeContext;